import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowLeft, GraduationCap, Clock, Award, CheckCircle2, ListChecks, FileText, Video, CheckSquare,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = { text: FileText, video: Video, checklist: CheckSquare, quiz: ListChecks };

export default function AcademyEnrollment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/academy/enrollments/${id}`); setD(r.data); }
    catch { setErr(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-center py-20 text-muted-foreground">Course not found.</div>;
  if (!d) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const { enrollment: e, course: c, can_edit } = d;

  const toggleLesson = async (l) => {
    if (!can_edit || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/academy/enrollments/${id}/lessons/${l.id}`, { completed: !l.completed });
      if (r.data.status === "completed" && e.status !== "completed") toast.success("Course completed — you're certified!");
      await load();
    } catch { toast.error("Failed"); }
    setBusy(false);
  };
  const toggleItem = async (x) => {
    if (!can_edit || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/academy/enrollments/${id}/checklist/${x.id}`, { completed: !x.completed });
      if (r.data.status === "completed" && e.status !== "completed") toast.success("Course completed — you're certified!");
      await load();
    } catch { toast.error("Failed"); }
    setBusy(false);
  };

  return (
    <div>
      <Button variant="ghost" size="sm" className="gap-2 mb-4 -ml-2 text-muted-foreground" onClick={() => navigate("/academy")} data-testid="enrollment-back"><ArrowLeft size={16} /> Back to My Academy</Button>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="h-14 w-14 rounded-2xl bg-neutral-900 flex items-center justify-center shrink-0"><GraduationCap size={28} className="text-primary-foreground" strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{c.name}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <StatusBadge value={e.certification_status} />
              {c.certification_type && <span className="text-xs text-muted-foreground flex items-center gap-1"><Award size={12} /> {c.certification_type}</span>}
              {c.estimated_duration && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> {c.estimated_duration}</span>}
            </div>
          </div>
        </div>
      </div>

      <Card className="border-border mb-6"><CardContent className="p-5">
        <div className="flex justify-between text-sm mb-1.5"><span className="font-medium">Your progress</span><span className="text-muted-foreground">{e.progress_percentage}%</span></div>
        <ProgressBar value={e.progress_percentage} className="h-2.5" data-testid="enrollment-progress" />
        {e.status === "completed" && <div className="flex items-center gap-2 text-sm text-green-700 mt-3"><CheckCircle2 size={16} /> Completed on {fmtDate(e.completed_at)}{e.expires_at ? ` · valid until ${fmtDate(e.expires_at)}` : ""}</div>}
        {!can_edit && <div className="text-xs text-muted-foreground mt-2">You are viewing this enrollment. Only the learner can mark progress.</div>}
      </CardContent></Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold mb-3">Modules</h2>
          <Accordion type="multiple" defaultValue={c.modules.map((m) => m.id)} className="space-y-2">
            {c.modules.map((m, i) => {
              const done = m.lessons.filter((l) => l.completed).length;
              return (
                <AccordionItem key={m.id} value={m.id} className="border border-border rounded-lg px-4 bg-card" data-testid="enrollment-module">
                  <AccordionTrigger className="hover:no-underline py-3">
                    <div className="flex items-center gap-3 text-left flex-1">
                      <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                      <div className="flex-1"><div className="font-medium text-sm">{m.title}</div><div className="text-xs text-muted-foreground">{done}/{m.lessons.length} lessons done</div></div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 space-y-2">
                    {m.description && <p className="text-sm text-muted-foreground mb-2">{m.description}</p>}
                    {m.lessons.map((l) => {
                      const Icon = TYPE_ICON[l.lesson_type] || FileText;
                      return (
                        <div key={l.id} className={`rounded-lg border p-3 flex items-start gap-3 ${l.completed ? "border-green-200 bg-green-50/50" : "border-border/60"}`} data-testid="enrollment-lesson">
                          <Checkbox checked={!!l.completed} onCheckedChange={() => toggleLesson(l)} disabled={!can_edit || busy} className="mt-0.5" data-testid={`lesson-check-${l.id}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2"><Icon size={14} className="text-primary" /><span className={`text-sm font-medium ${l.completed ? "line-through text-muted-foreground" : ""}`}>{l.title}</span><span className="text-[10px] text-muted-foreground capitalize">{l.lesson_type}</span></div>
                            {l.content && <p className="text-xs text-muted-foreground mt-1">{l.content}</p>}
                          </div>
                        </div>
                      );
                    })}
                    {m.lessons.length === 0 && <p className="text-xs text-muted-foreground">No lessons in this module.</p>}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div>
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><ListChecks size={16} strokeWidth={1.5} /> Certification Checklist</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {c.checklist.map((x) => (
                <div key={x.id} className={`rounded-lg border p-3 flex items-start gap-3 ${x.completed ? "border-green-200 bg-green-50/50" : "border-border/60"}`} data-testid="enrollment-checklist">
                  <Checkbox checked={!!x.completed} onCheckedChange={() => toggleItem(x)} disabled={!can_edit || busy} className="mt-0.5" data-testid={`checklist-check-${x.id}`} />
                  <div><div className={`text-sm font-medium ${x.completed ? "line-through text-muted-foreground" : ""}`}>{x.title}</div>{x.description && <div className="text-xs text-muted-foreground">{x.description}</div>}</div>
                </div>
              ))}
              {c.checklist.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No checklist for this course.</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
